// #region Imports
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { TextFieldProps } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddress from "_shared/email-addresses/email-address";
import EmailAddressSchema from "_shared/email-addresses/email-address-schema";
import Guid from "_shared/guids/guid";
import { Currency } from "_shared/money/currencies/currency";
import PhoneNumber from "_shared/phone-numbers/phone-number";
import UrlList from "_shared/urls/url-list";
import * as Config from "config";
import { E164Number } from "libphonenumber-js";
import AvatarUpload, { AvatarUploadValue } from "marketplace/_views/avatars/avatar-upload";
import ProfileCategoriesSelect from "marketplace/_views/profiles/profile-categories-select";
import VisibilityToggleSwitch, {
  buildVisibilityConfirmMessage,
  buildVisibilityConfirmTitle,
  buildVisibilitySuccessMessage,
} from "marketplace/_views/profiles/visibility-toggle-switch";
import ResumeUpload from "marketplace/_views/resume-upload";
import {
  getIndividualAvatar,
  getIndividualById,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { individualsApi, useGetIndividualAvatarQuery, useGetIndividualByIdQuery } from "marketplace/individuals/_api/individual-api";
import IndividualProfileEditName from "marketplace/individuals/profiles/_views/forms/edit/fields/individual-profile-name-field";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import InsuranceInfo from "marketplace/insurance-info/insurance-info";
import RegistrationNumber from "marketplace/registration-number/registration-number";
import BillingRateInfo from "marketplace/teams/fee-schedules/rates/billing-rate-info";
import { enqueueSnackbar } from "notistack";
import { forwardRef, memo, useDeferredValue, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSelector } from "react-redux";
// #endregion

type DraftTextFieldProps = Omit<TextFieldProps, "onChange" | "value"> & {
  value: string;
  onDraftChange?: (value: string) => void;
  onCommit?: (value: string) => void;
  commitDelay?: number;
};

const DraftTextField = memo(function DraftTextField(props: DraftTextFieldProps) {
  const {
    value,
    onDraftChange,
    onCommit,
    commitDelay: commitDelayProp,
    ...textFieldProps
  } = props;
  const commitDelay = commitDelayProp ?? 120;
  const [localEdit, setLocalEdit] = useState<{ sourceValue: string; value: string }>();
  const draftValue = localEdit?.sourceValue === value ? localEdit.value : value;
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => {
    if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
  }, []);

  return (
    <TextField
      {...textFieldProps}
      value={draftValue}
      onChange={(event) => {
        const nextValue = event.target.value;
        setLocalEdit({ sourceValue: value, value: nextValue });
        onDraftChange?.(nextValue);

        if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
        commitTimeoutRef.current = setTimeout(() => {
          commitTimeoutRef.current = undefined;
          onCommit?.(nextValue);
        }, commitDelay);
      }}
      onBlur={(event) => {
        if (commitTimeoutRef.current) {
          clearTimeout(commitTimeoutRef.current);
          commitTimeoutRef.current = undefined;
          onCommit?.(draftValue);
        }
        textFieldProps.onBlur?.(event);
      }}
    />
  );
});

// #region Styled Components
const MainContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  // The dialog hands the form a bounded height, so the fields scroll inside it and the save button
  // keeps a row of its own at the bottom instead of floating over whatever is under it.
  maxHeight: "100%",
  minHeight: 0,
  width: "100%",
}));
const FormScrollArea = styled("div")(({ theme }) => ({
  display: "grid",
  flex: "1 1 auto",
  gridTemplateRows: "repeat(4, auto)",
  minHeight: 0,
  overflowX: "hidden",
  overflowY: "auto",
  // MUI hangs a switch's control 11px outside its own row, so that the track lines up with the body
  // text beside it. This area hides its overflow, which cut the hover halo off at the edge — so the
  // room the control is designed to hang into is given here, where every row shares it and the
  // alignment the negative margin buys is kept.
  paddingLeft: theme.spacing(1.5),
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    "& > *": {
      margin: 0,
      padding: theme.spacing(0, 0, 3, 0),
      width: "100%",
    },
    "&:not(last-child)": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "row",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(0, 0, 4, 0),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
  },
  [theme.breakpoints.up("lg")]: {
    columnCount: 3,
  },
  columnFill: "balance",
  columnGap: theme.spacing(4),
  gridColumn: "1 / auto",
  gridRowStart: "2",
  paddingTop: theme.spacing(0.75),
}));
const FieldContainer = styled("div")(() => ({
  display: "inline-block",
  width: "100%",
}));
const CheckboxContainer = styled("div")(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const GridContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
}));
const Credentials = styled("section")(({ theme }) => ({
  gridColumn: "1 / span 3",
  gridRowStart: "3",
  marginTop: theme.spacing(1),
}));
const CredentialsTextField = styled(DraftTextField)(() => ({
  width: "100%",
}));
const AvatarNameContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
}));
const StyledDraftTextField = styled(DraftTextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const InsuredAmountField = styled(NumericFormat)<TextFieldProps>(({ theme }) => ({
  marginBottom: theme.spacing(2),
})) as typeof NumericFormat;
const BillingRateField = styled(NumericFormat)<TextFieldProps>(({ theme }) => ({
  marginBottom: theme.spacing(2),
})) as typeof NumericFormat;
const BillingUnitField = styled(DraftTextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const ContactInfoContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  flexDirection: "row",
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const ContactDetails = styled("div")(() => ({
  flex: 1,
  width: "100%",
}));
const PhoneField = styled(PhoneInputWithCountrySelect)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const DraftEmailField = styled(DraftTextField)(() => ({
  width: "100%",
}));
const OnCallCheckbox = styled(FormControlLabel)(() => ({
  margin: 0,
  position: "relative",
}));
const HelpIcon = styled(HelpOutlineIcon)(({ theme }) => ({
  color: "rgba(0, 0, 0, 0.5)",
  cursor: "help",
  marginLeft: theme.spacing(0.75),
}));
const TitleContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
}));
const ProfileDetails = styled("section")(({ theme }) => ({
  gridColumn: "1 / span 3",
  gridRowStart: "4",
  // Clear of the button row's divider, so the switch is not pressed against it.
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flex: "0 0 auto",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  padding: theme.spacing(2, 0),
  width: "100%",
}));
const SaveButtonWrapper = styled("span")(() => ({
  alignSelf: "flex-start",
  display: "inline-flex",
}));
const SaveValidationMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));
// #endregion

const TextFieldComponent = forwardRef<HTMLInputElement, TextFieldProps>(function TextFieldComponent(props, ref) {
  return <TextField {...props} inputRef={ref} />;
});

export interface EditIndividualProfileProps {
  individualId?: Guid;
  isVisible?: boolean;
  initialProfile?: IndividualProfile;
  onFormDirty?: (isDirty?: boolean) => void;
  onSave?: (profile: IndividualProfile) => void;
}

export default function EditIndividualProfile(props: Readonly<EditIndividualProfileProps>) {
  const { onFormDirty, onSave } = props;

  // #region State
  const [isSaving, setIsSaving] = useState(false);
  const [isVisible, setIsVisible] = useState<boolean>(props.isVisible ?? false);
  const [individualId] = useState<Guid | undefined>(props.individualId);
  const [currentProfile, setCurrentProfile] = useState<IndividualProfile | undefined>(props.initialProfile);
  const [updatedProfile, setUpdatedProfile] = useState<IndividualProfile | undefined>(props.initialProfile);
  const [updatedAvatar, setUpdatedAvatar] = useState<AvatarUploadValue | undefined>();
  const [updatedResume, setUpdatedResume] = useState<File | null | undefined>(undefined);
  const [formEmail, setFormEmail] = useState<string>("");
  const [emailDraftIsValid, setEmailDraftIsValid] = useState(true);
  const [formPhoneNumber, setFormPhoneNumber] = useState<E164Number | undefined>();
  const [formSchool, setFormSchool] = useState<string>("");
  const [formDegree, setFormDegree] = useState<string>("");
  const [formRegionsRegistered, setFormRegionsRegistered] = useState<string[]>([]);
  const [formRegionsLicensed, setFormRegionsLicensed] = useState<string[]>([]);
  const [formNumberOfApplicationsDraftedProsecuted, setFormNumberOfApplicationsDraftedProsecuted] =
    useState<string>("");
  const [formNumberOfAllowedApplications, setFormNumberOfAllowedApplications] = useState<string>("");
  const [formBarAdmissionYear, setFormBarAdmissionYear] = useState<string>("");
  const [formProfessionalRegistration, setFormProfessionalRegistration] = useState<string>("");
  const [formTechnicalBackgrounds, setFormTechnicalBackgrounds] = useState<string[]>([]);
  const [formPracticeAreas, setFormPracticeAreas] = useState<string[]>([]);
  const [formCredentials, setFormCredentials] = useState<string>(props.initialProfile?.credentials ?? "");
  const [formProfessionalPublications, setFormProfessionalPublications] = useState<string[]>([]);
  const [formSampleApplications, setFormSampleApplications] = useState<string[]>([]);
  const [formLinks, setFormLinks] = useState<string[]>([]);
  const [formTechnicalLanguages, setFormTechnicalLanguages] = useState<string[]>([]);
  const [formFluentLanguages, setFormFluentLanguages] = useState<string[]>([]);
  const [formInsuredAmount, setFormInsuredAmount] = useState<number>(0);
  const [formBillingRate, setFormBillingRate] = useState<string>("");
  const [formBillingUnit, setFormBillingUnit] = useState<string>("");
  const [formValidated, setFormValidated] = useState(() => {
    const initialProfile = props.initialProfile;
    return Boolean(
      initialProfile
        && (initialProfile.categories?.length ?? 0) > 0
        && initialProfile.firstName.length > 0
        && initialProfile.lastName.length > 0,
    );
  });
  const [profileHash, setProfileHash] = useState<string>(() =>
    props.initialProfile ? JSON.stringify(props.initialProfile.toJSON()) : "",
  );
  const hydratedProfileRef = useRef<IndividualProfile | undefined>(props.initialProfile);
  const latestUpdatedProfileRef = useRef<IndividualProfile | undefined>(props.initialProfile);
  const pendingProfileChangesRef = useRef<Record<string, unknown>>({});
  const pendingProfileValidationRef = useRef<boolean>(false);
  const profileCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingFormValueCommitRef = useRef<(() => void)[]>([]);
  const [hasPendingProfileCommit, setHasPendingProfileCommit] = useState(false);
  const [isProfileUpdatePending, startProfileUpdateTransition] = useTransition();
  const deferredUpdatedProfile = useDeferredValue(updatedProfile);

  // #region Hooks
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const countryOptions = useMemo(() => Config.countries.map((country) => country.name), []);
  const languageOptions = useMemo(() => Config.languages.map((language) => language.name), []);
  const technicalBackgroundOptions = useMemo(() => Config.technicalBackgrounds, []);
  const practiceAreaOptions = useMemo(() => Config.practiceAreas, []);
  const stateOptions = useMemo(() => Config.states.map((state) => state.name), []);

  // #region Individual Selectors
  const individual = useSelector(getIndividualById(individualId));
  const { isLoading: isLoadingIndividual, isError: errorLoadingIndividual } = useGetIndividualByIdQuery(
    { individualId: individualId?.value ?? "" },
    { skip: !individualId || !!individual },
  );

  // #region Avatar Selectors
  const avatar = useSelector(getIndividualAvatar(individualId ?? null, "small"));
  const { isLoading: isLoadingAvatar } = useGetIndividualAvatarQuery(
    { individualId: individualId?.value ?? "", size: "small" },
    { skip: !individualId || !individual?.profile?.avatarId || !!avatar },
  );

  const validateProfile = (profile: IndividualProfile) => {
    if (!profile) return;
    const hasCategories: boolean = (profile.categories?.length ?? 0) > 0;
    const hasName: boolean = profile.firstName.length > 0 && profile.lastName.length > 0;
    setFormValidated(hasCategories && hasName);
  };

  const validateProfileChanges = (changes: Record<string, unknown>) => {
    const profile = latestUpdatedProfileRef.current;
    if (!profile) return;

    const categories = (changes.categories as string[] | undefined) ?? profile.categories ?? [];
    const firstName = (changes.firstName as string | undefined) ?? profile.firstName;
    const lastName = (changes.lastName as string | undefined) ?? profile.lastName;

    setFormValidated(categories.length > 0 && firstName.length > 0 && lastName.length > 0);
  };

  function buildUpdatedProfile(changes: Record<string, unknown>): IndividualProfile | undefined {
    const latestProfile = latestUpdatedProfileRef.current;
    if (!latestProfile) return undefined;

    const profileChanges = { ...changes };

    if ("professionalRegistration" in profileChanges) {
      profileChanges.professionalRegistration = profileChanges.professionalRegistration
        ? new RegistrationNumber(profileChanges.professionalRegistration as string)
        : undefined;
    }
    if ("phoneNumber" in profileChanges) {
      profileChanges.phoneNumber = profileChanges.phoneNumber
        ? new PhoneNumber(profileChanges.phoneNumber as E164Number)
        : undefined;
    }
    if ("email" in profileChanges) {
      const email = profileChanges.email as string;
      profileChanges.email = EmailAddressSchema.isValidJSON(email) ? new EmailAddress(email) : undefined;
    }
    if ("insuredAmount" in profileChanges) {
      profileChanges.insured = InsuranceInfo.create({
        insuredAmount: (profileChanges.insuredAmount as number | undefined) ?? 0,
        currency: Currency.USD,
      });
      delete profileChanges.insuredAmount;
    }
    if ("billingRateValue" in profileChanges || "billingUnitValue" in profileChanges) {
      profileChanges.billingRate = new BillingRateInfo({
        billingRate: (profileChanges.billingRateValue as string | undefined) ?? latestProfile.billingRate?.billingRate ?? "0",
        billingUnit: (profileChanges.billingUnitValue as string | undefined) ?? latestProfile.billingRate?.billingUnit ?? "",
      });
      delete profileChanges.billingRateValue;
      delete profileChanges.billingUnitValue;
    }

    return new IndividualProfile({ ...latestProfile, ...profileChanges });
  }

  function flushPendingProfileChanges(): IndividualProfile | undefined {
    if (profileCommitTimeoutRef.current) {
      clearTimeout(profileCommitTimeoutRef.current);
      profileCommitTimeoutRef.current = undefined;
    }

    const pendingFormValueCommits = pendingFormValueCommitRef.current;
    pendingFormValueCommitRef.current = [];
    pendingFormValueCommits.forEach((commitFormValue) => commitFormValue());

    const changes = pendingProfileChangesRef.current;
    if (Object.keys(changes).length === 0) return latestUpdatedProfileRef.current;

    const shouldValidate = pendingProfileValidationRef.current;
    pendingProfileChangesRef.current = {};
    pendingProfileValidationRef.current = false;
    setHasPendingProfileCommit(false);

    const nextProfile = buildUpdatedProfile(changes);
    if (!nextProfile) return undefined;
    latestUpdatedProfileRef.current = nextProfile;

    if (shouldValidate) validateProfile(nextProfile);

    startProfileUpdateTransition(() => {
      setUpdatedProfile(nextProfile);
    });

    return nextProfile;
  }

  function scheduleProfileChanges(
    changes: Record<string, unknown>,
    options?: { commitFormValue?: () => void; validate?: boolean },
  ) {
    pendingProfileChangesRef.current = { ...pendingProfileChangesRef.current, ...changes };
    pendingProfileValidationRef.current = pendingProfileValidationRef.current || Boolean(options?.validate);
    if (options?.commitFormValue) pendingFormValueCommitRef.current.push(options.commitFormValue);

    if (options?.validate) validateProfileChanges({ ...latestUpdatedProfileRef.current, ...pendingProfileChangesRef.current });

    setHasPendingProfileCommit(true);

    if (profileCommitTimeoutRef.current) clearTimeout(profileCommitTimeoutRef.current);

    profileCommitTimeoutRef.current = setTimeout(() => {
      profileCommitTimeoutRef.current = undefined;
      flushPendingProfileChanges();
    }, 150);
  }

  const dirty = useMemo<boolean>(() => {
    if (hasPendingProfileCommit) {
      onFormDirty?.(true);
      return true;
    }
    if (!deferredUpdatedProfile) {
      onFormDirty?.(false);
      return false;
    }
    const hash = JSON.stringify(deferredUpdatedProfile.toJSON());
    const isDirty = hash !== profileHash || !!updatedAvatar || !!updatedResume;
    onFormDirty?.(isDirty);
    return isDirty;
  // We cannot guarantee onFormDirty being stable, if it changes, change the key to rerender
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredUpdatedProfile, hasPendingProfileCommit, profileHash, updatedAvatar, updatedResume]);

  /**
   * Visibility is a field of this form now rather than a switch that writes on its own, so it has to
   * count towards whether there is anything to save.
   */
  const isVisibilityDirty = Boolean(individualId) && isVisible !== (individual?.isVisible ?? false);
  const hasUnsavedChanges = dirty || isVisibilityDirty;

  const canSave = useMemo<boolean>(() =>
    !isLoadingIndividual &&
    !isLoadingAvatar &&
    !isSaving &&
    !isProfileUpdatePending &&
    hasUnsavedChanges &&
    formValidated &&
    emailDraftIsValid,
  [emailDraftIsValid, formValidated, hasUnsavedChanges, isLoadingAvatar, isLoadingIndividual, isProfileUpdatePending, isSaving]);

  const isProfileTypeSelected = (updatedProfile?.categories?.length ?? 0) > 0;
  const saveIsBlockedByMissingProfileType = Boolean(
    updatedProfile
      && !isProfileTypeSelected
      && !isLoadingIndividual
      && !isLoadingAvatar
      && !isSaving
      && dirty
      && updatedProfile.firstName.length > 0
      && updatedProfile.lastName.length > 0
      && emailDraftIsValid,
  );
  const saveValidationMessage = saveIsBlockedByMissingProfileType
    ? "Select at least one profile type to enable save."
    : "";

  function setFormValues(profile: IndividualProfile) {
    setFormEmail(profile.email?.value ?? "");
    setFormPhoneNumber(profile.phoneNumber?.toE164Number());
    setFormSchool(profile.school ?? "");
    setFormDegree(profile.degree ?? "");
    setFormProfessionalRegistration(profile.professionalRegistration?.value ?? "");
    setFormTechnicalBackgrounds(profile.technicalBackgrounds ?? []);
    setFormPracticeAreas(profile.practiceAreas ?? []);
    setFormCredentials(profile.credentials ?? "");
    setFormProfessionalPublications(profile.professionalPublications ?? []);
    setFormLinks(profile.links ?? []);
    setFormTechnicalLanguages(profile.technicalLanguages ?? []);
    setFormFluentLanguages(profile.fluentLanguages ?? []);
    setFormRegionsRegistered(profile.regionsRegistered ?? []);
    setFormRegionsLicensed(profile.regionsLicensed ?? []);
    setFormNumberOfApplicationsDraftedProsecuted(profile.numberOfApplicationsDraftedProsecuted?.toString() ?? "");
    setFormNumberOfAllowedApplications(profile.numberOfAllowedApplications?.toString() ?? "");
    setFormBarAdmissionYear(profile.barAdmissionYear?.toString() ?? "");
    setFormInsuredAmount(profile.insured?.insuredAmount ?? 0);
    setFormBillingRate(profile.billingRate?.billingRate ?? "");
    setFormBillingUnit(profile.billingRate?.billingUnit ?? "");
    setFormSampleApplications(profile.sampleApplications ?? []);
  }

  // #region Lifecycle
  useEffect(() => {
    if (!individual) return;
    if (!individual.profile) throw new Error("Individual profile not found");
    if (hydratedProfileRef.current === individual.profile) return;

    const hash = JSON.stringify(individual.profile.toJSON());
    let active = true;
    if (hash === profileHash) {
      hydratedProfileRef.current = individual.profile;
      queueMicrotask(() => {
        if (active) setIsVisible(individual.isVisible ?? false);
      });
      return () => {
        active = false;
      };
    }

    queueMicrotask(() => {
      if (!active) return;

      hydratedProfileRef.current = individual.profile;
      latestUpdatedProfileRef.current = individual.profile;
      setProfileHash(hash);
      setCurrentProfile(individual.profile);
      setFormValues(individual.profile);
      setIsVisible(individual.isVisible ?? false);
      setUpdatedProfile(individual.profile);
      validateProfile(individual.profile);
    });

    return () => {
      active = false;
    };
  }, [individual, profileHash]);

  useEffect(() => () => {
    if (profileCommitTimeoutRef.current) clearTimeout(profileCommitTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (errorLoadingIndividual) {
      enqueueSnackbar("Profile currently unavailable.", { variant: "warning" });
    }
  }, [errorLoadingIndividual]);
  // #endregion

  async function handleSaveButtonClick() {
    // Before the save: publishing to the marketplace is worth asking about, and the switch no longer
    // writes anything on its own, so this is the only point at which it takes effect.
    const maySave = await confirmVisibilityChange();
    if (!maySave) return;

    if (individualId) await updateProfile();
  }

  /** Asks before a save that would change who can see the profile. */
  async function confirmVisibilityChange(): Promise<boolean> {
    if (!isVisibilityDirty) return true;

    const profileName = `${updatedProfile?.firstName ?? ""} ${updatedProfile?.lastName ?? ""}`.trim() || "this";
    const response = await confirm({
      title: buildVisibilityConfirmTitle(isVisible),
      message: buildVisibilityConfirmMessage(profileName, isVisible),
    });

    return response !== ConfirmResponse.Cancel;
  }

  async function updateProfile() {
    if (!individualId || !currentProfile || !updatedProfile) return;

    const wasVisibilityChanged = isVisibilityDirty;
    const savedProfileName = `${updatedProfile.firstName ?? ""} ${updatedProfile.lastName ?? ""}`.trim() || "this";
    const savedVisibility = isVisible;

    const profileToSave = flushPendingProfileChanges() ?? updatedProfile;

    try {
      setIsSaving(true);
      if (updatedAvatar) {
        await createAvatar(individualId, updatedAvatar);
        setUpdatedAvatar(undefined);
      }
      if (updatedResume) {
        await createResume(individualId, updatedResume);
        setUpdatedResume(undefined);
      }
      else if (updatedResume === null)
      {
        await dispatch(individualsApi.endpoints.deleteIndividualResume.initiate({ individualId: individualId.value })).unwrap();
      }
      const returnedProfileJson = await dispatch(
        individualsApi.endpoints.updateIndividual.initiate({
          individualId: individualId.value,
          isVisible,
          originalProfile: currentProfile,
          updatedProfile: profileToSave,
        }),
      ).unwrap();
      const returnedProfile = IndividualProfile.fromJSON(returnedProfileJson);
      const returnedProfileHash = JSON.stringify(returnedProfile.toJSON());

      setCurrentProfile(returnedProfile);
      latestUpdatedProfileRef.current = returnedProfile;
      setUpdatedProfile(returnedProfile);
      setProfileHash(returnedProfileHash);
      setFormValues(returnedProfile);

      enqueueSnackbar("Profile changes saved", { variant: "success" });
      if (wasVisibilityChanged) {
        enqueueSnackbar(buildVisibilitySuccessMessage(savedProfileName, savedVisibility), { variant: "info" });
      }

      onSave?.(returnedProfile);
    } catch {
      enqueueSnackbar("Unable to update profile", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }


  async function createAvatar(individualId: Guid, avatar: AvatarUploadValue) {
    if (!individualId || !avatar) return;

    const result = await dispatch(individualsApi.endpoints.createIndividualAvatar.initiate({
      individualId: individualId.value,
      file: avatar.file,
      crop: avatar.crop,
    }));
    if ("error" in result) {
      enqueueSnackbar("Unable to create avatar", { variant: "error" });
    }
  }

  async function createResume(individualId: Guid, resume: File) {
    if (!individualId || !resume) return;

    const result = await dispatch(individualsApi.endpoints.createIndividualResume.initiate({ individualId: individualId.value, file: resume }));
    if ("error" in result) {
      enqueueSnackbar("Unable to create resume", { variant: "error" });
    }
  }

  function hideIfNotIPAttorney() {
    return {
      display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
    };
  }

  return (
    <MainContainer>
      <FormScrollArea data-testid="profile-form-fields">
        <HeaderContainer>
          <AvatarNameContainer>
            <AvatarUpload
              accept="image/bmp, image/gif, image/heic, image/heif, image/jpeg, image/png, image/webp"
              avatar={updatedAvatar ?? avatar}
              onUpload={(updatedAvatar) => setUpdatedAvatar(updatedAvatar)}
            />
            <IndividualProfileEditName
              isLoading={isLoadingIndividual}
              firstName={updatedProfile?.firstName ?? ""}
              lastName={updatedProfile?.lastName ?? ""}
              onNameChange={(firstName: string, lastName: string) => {
                if (!updatedProfile || (firstName === updatedProfile.firstName && lastName === updatedProfile.lastName))
                  return;

                scheduleProfileChanges({
                  firstName,
                  lastName,
                }, { validate: true });
              }}
            />
          </AvatarNameContainer>
          <ProfileCategoriesSelect
            options={Config.IndividualProfileTypes}
            categories={updatedProfile?.categories ?? []}
            onChange={async (updatedCategories: string[]) => {
              if (!updatedProfile) return;
              if (updatedCategories === updatedProfile.categories) return;
              scheduleProfileChanges({
                categories: updatedCategories,
              }, { validate: true });
            }}
          />
        </HeaderContainer>
        <ContentContainer>
          <StyledDraftTextField
            id="registrationNumber"
            variant="outlined"
            size="small"
            sx={hideIfNotIPAttorney()}
            label="Professional Registration #"
            value={formProfessionalRegistration}
            onDraftChange={(value) => {
              if (!updatedProfile) return;

              scheduleProfileChanges(
                { professionalRegistration: value },
                { commitFormValue: () => setFormProfessionalRegistration(value) },
              );
            }}
            onCommit={flushPendingProfileChanges}
          />
          <StyledAutocomplete
            id="patentRegions"
            multiple
            size="small"
            sx={hideIfNotIPAttorney()}
            value={formRegionsRegistered ?? []}
            options={countryOptions}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Regions Registered" />}
            onChange={(_event, value, _reason) => {
              if (!Array.isArray(value)) return;

              setFormRegionsRegistered(value as string[]);

              if (!updatedProfile) return;
              if (value === updatedProfile.regionsRegistered) return;

              scheduleProfileChanges({ regionsRegistered: value as string[] });
            }}
          />
          {/*
            freeSolo because the vocabulary is a suggestion, not a constraint. Profiles written
            before it hold values like "Electrical" that are not list entries, and an editor that
            silently dropped them on the next save would lose data the owner never chose to change.
          */}
          <StyledAutocomplete
            id="technicalBackgrounds"
            multiple
            freeSolo
            size="small"
            onChange={(_event, value, _reason) => {
              if (!Array.isArray(value)) return;
              setFormTechnicalBackgrounds(value as string[]);
              if (!updatedProfile) return;
              if (value === updatedProfile.technicalBackgrounds) return;
              scheduleProfileChanges({ technicalBackgrounds: value as string[] });
            }}
            value={formTechnicalBackgrounds ?? []}
            options={technicalBackgroundOptions}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Technical Background" />}
          />
          <StyledAutocomplete
            id="practiceAreas"
            multiple
            size="small"
            onChange={(_event, value, _reason) => {
              if (!Array.isArray(value)) return;
              setFormPracticeAreas(value as string[]);
              if (!updatedProfile) return;
              if (value === updatedProfile.practiceAreas) return;
              scheduleProfileChanges({ practiceAreas: value as string[] });
            }}
            value={formPracticeAreas ?? []}
            options={practiceAreaOptions}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Practice Area" />}
          />
          <StyledDraftTextField
            id="school"
            variant="outlined"
            size="small"
            label="Undergraduate School / Law School"
            value={formSchool ?? ""}
            slotProps={{
              inputLabel: {
                shrink: Boolean(updatedProfile?.school),
              },
            }}
            onDraftChange={(value) => {
              if (!updatedProfile) return;
              scheduleProfileChanges(
                { school: value },
                { commitFormValue: () => setFormSchool(value) },
              );
            }}
            onCommit={flushPendingProfileChanges}
          />
          <StyledDraftTextField
            id="degree"
            variant="outlined"
            size="small"
            label="Degree(s)"
            value={formDegree ?? ""}
            slotProps={{
              inputLabel: {
                shrink: Boolean(updatedProfile?.degree),
              },
            }}
            onDraftChange={(value) => {
              if (!updatedProfile) return;
              scheduleProfileChanges(
                { degree: value },
                { commitFormValue: () => setFormDegree(value) },
              );
            }}
            onCommit={flushPendingProfileChanges}
          />
          <StyledDraftTextField
            id="numberDrafted"
            variant="outlined"
            type="number"
            size="small"
            sx={hideIfNotIPAttorney()}
            label="Number Applications Drafted / Prosecuted"
            slotProps={{
              inputLabel: {
                shrink: formNumberOfApplicationsDraftedProsecuted !== "",
              },
            }}
            value={formNumberOfApplicationsDraftedProsecuted ?? ""}
            onDraftChange={(value) => {
              if (!updatedProfile) return;
              let newValue: number | undefined;
              if (value !== "") newValue = Number.parseInt(value);
              scheduleProfileChanges(
                { numberOfApplicationsDraftedProsecuted: newValue },
                { commitFormValue: () => setFormNumberOfApplicationsDraftedProsecuted(value) },
              );
            }}
            onCommit={flushPendingProfileChanges}
          />
          <StyledDraftTextField
            id="numberAllowedApplications"
            variant="outlined"
            type="number"
            size="small"
            sx={hideIfNotIPAttorney()}
            label="Number Allowed Applications"
            slotProps={{
              inputLabel: {
                shrink: formNumberOfAllowedApplications !== "",
              },
            }}
            value={formNumberOfAllowedApplications ?? ""}
            onDraftChange={(value) => {
              if (!updatedProfile) return;
              let newValue: number | undefined;
              if (value !== "") newValue = Number.parseInt(value);
              scheduleProfileChanges(
                { numberOfAllowedApplications: newValue },
                { commitFormValue: () => setFormNumberOfAllowedApplications(value) },
              );
            }}
            onCommit={flushPendingProfileChanges}
          />
          <StyledDraftTextField
            id="yearAdmitted"
            variant="outlined"
            size="small"
            sx={hideIfNotIPAttorney()}
            type="number"
            label="Year Of Bar Admission"
            slotProps={{
              inputLabel: {
                shrink: formBarAdmissionYear !== "",
              },
            }}
            value={formBarAdmissionYear ?? ""}
            onDraftChange={(value) => {
              if (!updatedProfile) return;
              let newValue: number | undefined;
              if (value !== "") newValue = Number.parseInt(value);
              scheduleProfileChanges(
                { barAdmissionYear: newValue },
                { commitFormValue: () => setFormBarAdmissionYear(value) },
              );
            }}
            onCommit={flushPendingProfileChanges}
          />
          <StyledAutocomplete
            id="fluentLanguages"
            multiple
            size="small"
            onChange={(_event, value, _reason) => {
              if (!Array.isArray(value)) return;
              setFormFluentLanguages(value as string[]);
              if (!updatedProfile) return;
              if (value === updatedProfile.fluentLanguages) return;
              scheduleProfileChanges({ fluentLanguages: value as string[] });
            }}
            value={formFluentLanguages ?? []}
            options={languageOptions}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Fluent Languages" />}
          />
          <StyledAutocomplete
            id="technicalLanguages"
            size="small"
            multiple
            onChange={(_event, value, _reason) => {
              if (!Array.isArray(value)) return;
              setFormTechnicalLanguages(value as string[]);
              if (!updatedProfile) return;
              if (value === updatedProfile.technicalLanguages) return;
              scheduleProfileChanges({ technicalLanguages: value as string[] });
            }}
            value={formTechnicalLanguages ?? []}
            options={languageOptions}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Technical Languages" />}
          />
          <StyledAutocomplete
            id="states"
            size="small"
            sx={hideIfNotIPAttorney()}
            multiple
            onChange={(_event, value, _reason) => {
              if (!Array.isArray(value)) return;
              setFormRegionsLicensed(value as string[]);
              if (!updatedProfile) return;
              if (value === updatedProfile.regionsLicensed) return;
              scheduleProfileChanges({ regionsLicensed: value as string[] });
            }}
            value={formRegionsLicensed ?? []}
            options={stateOptions}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="States Licensed In" />}
          />
          <GridContainer>
            <InsuredAmountField
              id="insuredAmount"
              customInput={TextField}
              size="small"
              sx={{
                display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
              }}
              variant="outlined"
              label="Insured Amount"
              value={formInsuredAmount ?? 0}
              prefix="$"
              thousandSeparator=","
              onValueChange={(value: NumberFormatValues) => {
                setFormInsuredAmount(value.floatValue ?? 0);
                if (!updatedProfile) return;
                if (value.floatValue === updatedProfile.insured?.insuredAmount) return;
                scheduleProfileChanges({ insuredAmount: value.floatValue ?? 0 });
              }}
            />
          </GridContainer>
          <GridContainer>
            <BillingRateField
              id="billingRate"
              customInput={TextField}
              variant="outlined"
              label="Billing Rate"
              size="small"
              sx={{
                display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
              }}
              value={formBillingRate}
              prefix="$"
              thousandSeparator=","
              onValueChange={(value: NumberFormatValues) => {
                setFormBillingRate(value.value);
                if (!updatedProfile) return;
                if (value.floatValue === updatedProfile.billingRate?.billingRate) return;
                scheduleProfileChanges({ billingRateValue: value.value ?? "0" });
              }}
            />
            <BillingUnitField
              id="billingUnit"
              variant="outlined"
              label="Billing Unit"
              size="small"
              sx={{
                display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
              }}
              value={formBillingUnit}
              onDraftChange={(value) => {
                if (!updatedProfile) return;
                scheduleProfileChanges(
                  { billingUnitValue: value ?? "" },
                  { commitFormValue: () => setFormBillingUnit(value) },
                );
              }}
              onCommit={flushPendingProfileChanges}
            />
          </GridContainer>
          <ContactInfoContainer>
            <ContactDetails>
              <PhoneField
                id="phoneNumber"
                international
                defaultCountry="US"
                countryCallingCodeEditable={false}
                size="small"
                variant="outlined"
                value={formPhoneNumber}
                label="Phone Number"
                autoComplete="tel"
                inputComponent={TextFieldComponent}
                onChange={(phoneNumber) => {
                  setFormPhoneNumber(phoneNumber);
                  if (!updatedProfile) return;
                  if (phoneNumber?.toString() === updatedProfile.phoneNumber?.value?.toString()) return;
                  scheduleProfileChanges({ phoneNumber });
                }}
              />
              <DraftEmailField
                id="email"
                size="small"
                variant="outlined"
                value={formEmail ?? ""}
                label="Email Address"
                autoComplete="email"
                error={formEmail.length > 0 && !EmailAddressSchema.isValidJSON(formEmail)}
                onDraftChange={(value) => {
                  if (!updatedProfile) return;
                  setEmailDraftIsValid(value.length === 0 || EmailAddressSchema.isValidJSON(value));
                  scheduleProfileChanges(
                    { email: value },
                    { commitFormValue: () => setFormEmail(value) },
                  );
                }}
                onCommit={flushPendingProfileChanges}
              />
            </ContactDetails>
            <OnCallCheckbox
              id="onCallCheckbox"
              control={
                <Checkbox
                  color="primary"
                  checked={
                    (updatedProfile?.isOnCall &&
                      (Boolean(updatedProfile.phoneNumber) || Boolean(updatedProfile.email))) ??
                    false
                  }
                />
              }
              label="On-Call"
              labelPlacement="end"
              disabled={!updatedProfile?.phoneNumber && !updatedProfile?.email}
              onChange={(event) => {
                if (!updatedProfile) return;
                const isChecked: boolean = (event.target as HTMLInputElement).checked;
                if (isChecked === updatedProfile.isOnCall) return;
                scheduleProfileChanges({ isOnCall: isChecked });
              }}
            />
          </ContactInfoContainer>
          <CheckboxContainer>
            <FormControlLabel
              id="diversityCandidate"
              control={
                <Checkbox
                  value="diversityCandidate"
                  color="primary"
                  checked={updatedProfile?.potentialDiversityCandidate ?? false}
                />
              }
              label={<Typography>Potential Diversity Candidate</Typography>}
              labelPlacement="end"
              onChange={(event) => {
                if (!updatedProfile) return;
                const isChecked: boolean = (event.target as HTMLInputElement).checked;
                if (isChecked === updatedProfile.potentialDiversityCandidate) return;
                scheduleProfileChanges({ potentialDiversityCandidate: isChecked });
              }}
            />
            <FormControlLabel
              id="collaborate"
              control={
                <Checkbox value="willingToCollaborate" color="primary" checked={updatedProfile?.collaborates ?? false} />
              }
              label={<Typography>Collaborate With Other Individuals</Typography>}
              labelPlacement="end"
              onChange={(event) => {
                if (!updatedProfile) return;
                const isChecked: boolean = (event.target as HTMLInputElement).checked;
                if (isChecked === updatedProfile.collaborates) return;
                scheduleProfileChanges({ collaborates: isChecked });
              }}
            />
          </CheckboxContainer>
          <FieldContainer>
            <TitleContainer>
              <Typography variant="h6">Professional Publications</Typography>
              <Tooltip title="Enter a URL to display publications you've written">
                <HelpIcon fontSize="small" />
              </Tooltip>
            </TitleContainer>
            <UrlList
              label="Website URL"
              initialLinks={formProfessionalPublications ?? []}
              onChange={(updatedList: string[]) => {
                setFormProfessionalPublications(updatedList);
                if (!updatedProfile) return;
                if (updatedList === updatedProfile.professionalPublications) return;
                scheduleProfileChanges({ professionalPublications: updatedList });
              }}
            />
          </FieldContainer>
          {updatedProfile?.categories?.includes("IPAttorney") && (
            <FieldContainer>
              <TitleContainer>
                <Typography variant="h6">Sample Applications</Typography>
                <Tooltip title="Enter a URL address from Espacenet, Google Patents, etc to display applications you've written">
                  <HelpIcon fontSize="small" />
                </Tooltip>
              </TitleContainer>
              <UrlList
                label="Website URL or patent number"
                initialLinks={formSampleApplications ?? []}
                onChange={(applicationValues: string[]) => {
                  setFormSampleApplications(applicationValues);
                  if (!updatedProfile) return;
                  if (applicationValues === updatedProfile.sampleApplications) return;
                  scheduleProfileChanges({ sampleApplications: applicationValues });
                }}
              />
            </FieldContainer>
          )}
          <FieldContainer>
            <Typography variant="h6">Other Links</Typography>
            <UrlList
              label="Website URL"
              initialLinks={formLinks ?? []}
              onChange={(linkValues: string[]) => {
                setFormLinks(linkValues);
                if (!updatedProfile) return;
                if (linkValues === updatedProfile.links) return;
                scheduleProfileChanges({ links: linkValues });
              }}
            />
          </FieldContainer>
          <FieldContainer>
            <Typography variant="h6">Resume</Typography>
            <ResumeUpload
              individualId={individualId}
              accept=".doc,.docx,.pdf,.rtf,.odt"
              resume={updatedProfile?.resumeId ?? updatedResume}
              userName={`${updatedProfile?.firstName} ${updatedProfile?.lastName}`}
              onUpload={(updatedResume) => setUpdatedResume(updatedResume)}
              onDelete={() => {
                if (!individualId) {
                  setUpdatedResume(null);
                  return;
                }
                setUpdatedResume(undefined);
                if (!updatedProfile) return;
                scheduleProfileChanges({ resumeId: undefined });
              }}
            />
          </FieldContainer>
        </ContentContainer>
        <Credentials>
          <Typography variant="h6">Credentials</Typography>
          <CredentialsTextField
            id="credentials-textfield"
            value={formCredentials}
            onDraftChange={(value) => {
              if (!updatedProfile) return;
              scheduleProfileChanges(
                { credentials: value },
                { commitFormValue: () => setFormCredentials(value) },
              );
            }}
            onCommit={flushPendingProfileChanges}
            multiline={true}
            minRows={4}
            placeholder="Additional info displayed on your profile"
            variant="outlined"
          />
        </Credentials>
        <ProfileDetails>
          {individualId && (
            <VisibilityToggleSwitch
              label="Show Profile On Marketplace"
              checked={isVisible}
              disabled={isSaving || isLoadingIndividual}
              onChange={(nextIsVisible) => {
                setIsVisible(nextIsVisible);
              }}
            />
          )}
        </ProfileDetails>
      </FormScrollArea>
      <ButtonContainer>
        <Tooltip title={saveValidationMessage} placement="top">
          <SaveButtonWrapper>
            <LoadingButton
              id="saveButton"
              variant="contained"
              startIcon={<SaveIcon />}
              loading={isSaving}
              color="primary"
              disabled={!canSave}
              onClick={handleSaveButtonClick}
            >
              {individualId ? "Save Changes" : "Create Profile"}
            </LoadingButton>
          </SaveButtonWrapper>
        </Tooltip>
        {saveValidationMessage && (
          <SaveValidationMessage variant="caption">
            {saveValidationMessage}
          </SaveValidationMessage>
        )}
      </ButtonContainer>
    </MainContainer>
  );
}
